import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Lock, TrainerHintIcon } from '~/Components/Icons';
import { AccessList, useHasAccess } from '~/Hooks';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { ExtendedEvent } from '../ExtendedEvent.model';
import { EditEventGroupButton } from './EditEventGroupButton';
import { ArchiveEventGroupButton } from './ArchiveEventGroupButton';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import {
  IItemCreationSubsectionInputProps,
  ItemCreationSubsection,
  ItemCreationToolTipProps,
} from '~/Components';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { useGeneralSectionInputs } from './hooks';
import { EventType } from '~/Models/DTO/Event.model';
import { useScheduleSectionInputs } from './hooks/useScheduleSectionInputs';
import { Typography } from '~/UIKit';

export interface IGeneralSection {
  eventGroup: ExtendedEventGroup;
  event?: ExtendedEvent;
  bookingSummary?: IBookingSummaryEvent;
  isLoading?: boolean;
  eventType: EventType;
  liveStreamEnabled?: boolean;
}

export const GeneralSection = ({
  eventGroup,
  event,
  bookingSummary,
  isLoading = false,
  eventType,
  liveStreamEnabled,
}: IGeneralSection) => {
  const { hasAccess } = useHasAccess();
  const { t } = useTranslation();
  const boundariesElement = useRef(null);
  const generalSectionInputs = useGeneralSectionInputs({
    isLoading,
    eventGroup,
    eventType,
    bookingSummary,
    event,
    liveStreamEnabled,
  });

  const statusSectionInputs: IItemCreationSubsectionInputProps<Partial<ExtendedEventGroup>>[] = [
    {
      icon: <Lock />,
      label: t(eventGroup.public ? 'form.field.public' : 'form.field.private'),
      type: ItemCreationValidInput.SWITCHER,
      name: 'public',
    },
  ];

  const eventScheduleSectionInputs = useScheduleSectionInputs({
    event,
    eventType,
    bookingSummary,
  });

  const freeTrainingTooltip: ItemCreationToolTipProps = {
    title: t('event.freeTrainingHint'),
  };

  return (
    <div ref={boundariesElement}>
      <ItemCreationSubsection
        title={t('app.title.generalInformation')}
        isHeaderVisible={hasAccess(AccessList.SHOW_TRAINER_WARNING)}
        items={generalSectionInputs}
        tooltip={
          hasAccess(AccessList.SHOW_TRAINER_WARNING) &&
          eventType !== EventType.FREE_TRAINING && {
            title: t('event.trainerHint'),
            icon: <TrainerHintIcon />,
            boundariesElement: boundariesElement.current,
          }
        }
      />
      {!!event && (
        <ItemCreationSubsection
          title={t('app.title.schedule')}
          isHeaderVisible
          items={eventScheduleSectionInputs}
          tooltip={
            hasAccess(AccessList.SHOW_BOOKED_EVENT_SCHEDULE_EDIT_WARNING) &&
            !!bookingSummary?.bookingsBooked && {
              title: (
                <>
                  <Typography align="center" variant="h4">
                    {t('event.hasBookings.time.tooltip.title')}
                  </Typography>
                  <Typography align="left" variant="body2">
                    {t('event.hasBookings.time.tooltip.text')}
                  </Typography>
                  {[1, 2].map((optionNumber, i) => (
                    <Typography align="left" variant="body2" key={optionNumber}>
                      {i + 1}. {t(`event.hasBookings.time.tooltip.option${optionNumber}`)}
                    </Typography>
                  ))}
                </>
              ),
            }
          }
        />
      )}
      {(hasAccess(AccessList.BOOKING_TOOL_INTEGRATION) && eventType !== EventType.FREE_TRAINING) ||
        (hasAccess(AccessList.HIDDEN_FREE_TRAINING) && eventType === EventType.FREE_TRAINING && (
          <ItemCreationSubsection
            title={t('form.title.status')}
            isHeaderVisible
            items={statusSectionInputs}
            tooltip={freeTrainingTooltip}
          />
        ))}
      {!event && eventGroup?.id && <ArchiveEventGroupButton eventGroup={eventGroup} />}
      {!!event && (
        <EditEventGroupButton
          disabled={isLoading}
          eventGroup={eventGroup}
          eventType={event.type}
          firstPage
        />
      )}
    </div>
  );
};
